import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';
import EmptyState, { IconShield } from '../components/EmptyState';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
};

const COLS = '2fr 1.5fr 1.2fr 1fr 1fr 220px';

const EMPTY = {
  tenant_id: '', property_id: '', amount: '', paid_date: '', status: 'hinterlegt',
  return_date: '', return_amount: '', notes: '',
};

const STATUS_LABEL = {
  hinterlegt:    'Hinterlegt',
  teilweise:     'Teilw. zurück',
  zurückgezahlt: 'Zurückgezahlt',
};

const inputStyle = {
  width: '100%', border: `1px solid ${T.border}`, borderRadius: 8,
  padding: '10px 12px', fontSize: '13px', boxSizing: 'border-box',
  color: T.ink, background: 'white', outline: 'none',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtEur(val) {
  if (!val && val !== 0) return '–';
  return `${Number(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export default function Deposits() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const [deposits, setDeposits]     = useState([]);
  const [tenants, setTenants]       = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [filterStatus, setFilterStatus] = useState('alle');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data: d }, { data: t }, { data: p }] = await Promise.all([
      supabase.from('deposits').select('*').eq('owner_id', user.id).order('created_at', { ascending: false }),
      supabase.from('tenants').select('id, first_name, last_name, unit, property_id').eq('owner_id', user.id).order('last_name'),
      supabase.from('properties').select('id, name').eq('owner_id', user.id).order('name'),
    ]);
    setDeposits(d || []);
    setTenants(t || []);
    setProperties(p || []);
    setLoading(false);
  }

  function tenantById(id) { return tenants.find(t => t.id === id); }
  function tenantName(id) {
    const t = tenantById(id);
    return t ? `${t.last_name}, ${t.first_name}` : '–';
  }
  function propertyName(id) {
    if (!id) return null;
    return properties.find(p => p.id === id)?.name || null;
  }

  function handleTenantChange(tenantId) {
    const tenant = tenantById(tenantId);
    setForm(prev => ({ ...prev, tenant_id: tenantId, property_id: prev.property_id || tenant?.property_id || '' }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const payload = {
      tenant_id:     form.tenant_id     || null,
      property_id:   form.property_id   || null,
      amount:        form.amount        !== '' ? Number(form.amount)        : null,
      paid_date:     form.paid_date     || null,
      status:        form.status,
      return_date:   form.return_date   || null,
      return_amount: form.return_amount !== '' ? Number(form.return_amount) : null,
      notes:         form.notes         || null,
      owner_id:      user.id,
    };

    let err;
    if (editing) {
      ({ error: err } = await supabase.from('deposits').update(payload).eq('id', editing).eq('owner_id', user.id));
    } else {
      ({ error: err } = await supabase.from('deposits').insert(payload));
    }

    if (err) { setError(err.message); setSaving(false); return; }
    await fetchAll();
    closeForm();
    setSaving(false);
    addToast(editing ? 'Kaution aktualisiert.' : 'Kaution hinzugefügt.', 'success');
  }

  async function deleteDeposit(id) {
    if (!window.confirm('Kaution wirklich löschen?')) return;
    await supabase.from('deposits').delete().eq('id', id).eq('owner_id', user.id);
    setDeposits(d => d.filter(x => x.id !== id));
    addToast('Kaution gelöscht.', 'success');
  }

  async function handleQuickReceive(deposit) {
    const today = new Date().toISOString().split('T')[0];
    await supabase.from('deposits').update({ status: 'hinterlegt', paid_date: deposit.paid_date || today }).eq('id', deposit.id).eq('owner_id', user.id);
    await fetchAll();
    addToast('Kaution als erhalten markiert.', 'success');
  }

  async function handleQuickReturn(deposit) {
    const today = new Date().toISOString().split('T')[0];
    await supabase.from('deposits').update({ status: 'zurückgezahlt', return_date: deposit.return_date || today }).eq('id', deposit.id).eq('owner_id', user.id);
    await fetchAll();
    addToast('Kaution zurückgezahlt.', 'success');
  }

  function openEdit(d) {
    setEditing(d.id);
    setForm({
      tenant_id:     d.tenant_id     || '',
      property_id:   d.property_id   || '',
      amount:        d.amount        ?? '',
      paid_date:     d.paid_date     || '',
      status:        d.status        || 'hinterlegt',
      return_date:   d.return_date   || '',
      return_amount: d.return_amount ?? '',
      notes:         d.notes         || '',
    });
    setError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
    setError(null);
  }

  function resolveProperty(deposit) {
    if (deposit.property_id) return propertyName(deposit.property_id);
    const tenant = tenantById(deposit.tenant_id);
    if (tenant?.property_id) return propertyName(tenant.property_id);
    return null;
  }

  function exportCSV() {
    const header = ['Mieter', 'Objekt', 'Betrag', 'Eingezahlt am', 'Status', 'Rückzahlung', 'Rückzahlungsbetrag', 'Notizen'];
    const rows = deposits.map(d => {
      const t = tenantById(d.tenant_id);
      return [
        t ? `${t.last_name}, ${t.first_name}` : '–',
        resolveProperty(d) || '–',
        d.amount || '',
        d.paid_date || '',
        STATUS_LABEL[d.status] || d.status,
        d.return_date || '',
        d.return_amount || '',
        d.notes || '',
      ];
    });
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'kautionen.csv'; a.click();
    URL.revokeObjectURL(url);
    addToast('CSV exportiert.', 'success');
  }

  const totalAmount  = deposits.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const activeCount  = deposits.filter(d => d.status === 'hinterlegt').length;
  const partialCount = deposits.filter(d => d.status === 'teilweise').length;

  const filtered = deposits.filter(d => filterStatus === 'alle' || d.status === filterStatus);

  const FILTER_TABS = [
    { val: 'alle',          label: 'Alle' },
    { val: 'hinterlegt',    label: 'Hinterlegt' },
    { val: 'teilweise',     label: 'Teilweise' },
    { val: 'zurückgezahlt', label: 'Zurückgez.' },
  ];

  return (
    <div style={{ maxWidth: 1100, color: T.ink }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: 32, gap: 16,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.6px', color: T.ink }}>
            Kautionen
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 14, color: T.muted }}>Alle Mietkautionen im Überblick.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {deposits.length > 0 && (
            <button onClick={exportCSV} className="btn-secondary" style={{ fontSize: 12, height: 38 }}>CSV</button>
          )}
          <button
            onClick={() => { setEditing(null); setForm(EMPTY); setError(null); setShowForm(true); }}
            className="btn-primary"
            style={{ fontSize: 13, height: 40, padding: '0 20px' }}
          >
            + Kaution anlegen
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        <div style={{ background: 'white', border: '1.5px solid #bbf7d0', borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(21,128,61,0.06)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#15803d', marginBottom: 8 }}>Hinterlegt</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#15803d', letterSpacing: '-1px', lineHeight: 1 }}>{activeCount}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>Aktive Kautionen</div>
        </div>
        <div style={{ background: partialCount > 0 ? '#fffbeb' : 'white', border: `1.5px solid ${partialCount > 0 ? '#fde68a' : T.border}`, borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: partialCount > 0 ? '#b45309' : T.muted, marginBottom: 8 }}>Teilw. zurück</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: partialCount > 0 ? '#b45309' : T.muted, letterSpacing: '-1px', lineHeight: 1 }}>{partialCount}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>Teilweise zurückgezahlt</div>
        </div>
        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: T.muted, marginBottom: 8 }}>Gesamtvolumen</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: '-0.5px', lineHeight: 1 }}>{fmtEur(totalAmount)}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{deposits.length} Kautionen gesamt</div>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {FILTER_TABS.map(({ val, label }) => (
          <button key={val} onClick={() => setFilterStatus(val)} className={`filter-tab${filterStatus === val ? ' active' : ''}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          {[1,2,3].map(n => (
            <div key={n} style={{ padding: '18px 24px', borderBottom: n < 3 ? `1px solid ${T.border}` : 'none', display: 'flex', gap: 16, alignItems: 'center' }}>
              <div className="skeleton" style={{ flex: 2, height: 14, borderRadius: 6 }} />
              <div className="skeleton" style={{ flex: 1, height: 14, borderRadius: 6 }} />
              <div className="skeleton" style={{ flex: 1, height: 14, borderRadius: 6 }} />
              <div className="skeleton" style={{ width: 80, height: 28, borderRadius: 8, marginLeft: 'auto' }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        deposits.length === 0 ? (
          <div style={{ background: 'white', border: `1.5px dashed ${T.border}`, borderRadius: 16, padding: '64px 48px', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, background: '#f1f5f9', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.ink }}>Keine Kautionen vorhanden</h3>
            <p style={{ margin: '8px auto 24px', fontSize: 13, color: T.muted, maxWidth: 300, lineHeight: 1.65 }}>
              Erfasse die erste Kaution für einen deiner Mieter.
            </p>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ fontSize: 13, height: 40, padding: '0 22px' }}>
              + Kaution anlegen
            </button>
          </div>
        ) : (
          <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, padding: 40, textAlign: 'center', fontSize: 13, color: T.muted }}>
            Keine Einträge in dieser Kategorie.
          </div>
        )
      ) : isMobile ? (
        /* ── Mobile cards ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(d => {
            const prop = resolveProperty(d);
            const badgeCls = d.status === 'hinterlegt' ? 'badge-green' : d.status === 'zurückgezahlt' ? 'badge-gray' : 'badge-yellow';
            const canReturn = d.status === 'hinterlegt' || d.status === 'teilweise';
            return (
              <div key={d.id} style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{tenantName(d.tenant_id)}</div>
                    {prop && <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{prop}</div>}
                  </div>
                  <span className={`badge ${badgeCls}`} style={{ fontSize: 12, padding: '4px 10px' }}>{STATUS_LABEL[d.status] || d.status}</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: T.ink, letterSpacing: '-0.5px', marginBottom: 4 }}>{fmtEur(d.amount)}</div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 14 }}>
                  Einzahlung: {fmt(d.paid_date)}
                  {d.return_date ? ` · Rückzahlung: ${fmt(d.return_date)}` : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {canReturn && (
                    <button onClick={() => handleQuickReturn(d)} style={{
                      fontSize: 13, height: 42, cursor: 'pointer', fontWeight: 600,
                      background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 8, color: '#b45309',
                    }}>
                      Kaution zurückzahlen
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(d)} className="btn-ghost" style={{ flex: 1, fontSize: 13, height: 38 }}>Bearbeiten</button>
                    <button onClick={() => deleteDeposit(d.id)} style={{ height: 38, padding: '0 14px', fontSize: 12, background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: 'pointer' }}>Löschen</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Desktop table ── */
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '12px 24px', borderBottom: `1px solid ${T.border}`, background: '#fafafa' }}>
            {['Mieter', 'Objekt', 'Betrag', 'Einzahlung', 'Status', ''].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
            ))}
          </div>
          {filtered.map((d, i) => {
            const prop = resolveProperty(d);
            const badgeCls = d.status === 'hinterlegt' ? 'badge-green' : d.status === 'zurückgezahlt' ? 'badge-gray' : 'badge-yellow';
            const canReturn = d.status === 'hinterlegt' || d.status === 'teilweise';
            return (
              <div key={d.id} style={{
                display: 'grid', gridTemplateColumns: COLS,
                padding: '16px 24px', alignItems: 'center',
                borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background 0.12s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{tenantName(d.tenant_id)}</div>
                </div>
                <div style={{ fontSize: 13, color: T.muted }}>
                  {prop ? <span style={{ fontWeight: 600, color: T.ink }}>{prop}</span> : <span>–</span>}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, letterSpacing: '-0.3px' }}>{fmtEur(d.amount)}</div>
                  {d.return_amount && d.return_amount !== d.amount && (
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>Zurück: {fmtEur(d.return_amount)}</div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>
                  {fmt(d.paid_date)}
                  {d.return_date && <div style={{ marginTop: 2 }}>Rück: {fmt(d.return_date)}</div>}
                </div>
                <div>
                  <span className={`badge ${badgeCls}`} style={{ fontSize: 12, padding: '4px 10px' }}>{STATUS_LABEL[d.status] || d.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                  {canReturn && (
                    <button onClick={() => handleQuickReturn(d)} style={{
                      fontSize: 12, height: 36, padding: '0 12px', cursor: 'pointer', fontWeight: 600,
                      background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 8, color: '#b45309',
                      whiteSpace: 'nowrap', transition: 'background 0.12s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
                    >
                      Zurückzahlen
                    </button>
                  )}
                  <button onClick={() => openEdit(d)} className="btn-ghost" style={{ fontSize: 12, height: 36, padding: '0 12px' }}>
                    Bearbeiten
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '24px',
        }}
          onClick={e => { if (e.target === e.currentTarget) closeForm(); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  {editing ? 'Kaution bearbeiten' : 'Neue Kaution'}
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, letterSpacing: '-0.4px', color: T.ink }}>
                  {editing ? 'Kaution bearbeiten' : 'Kaution anlegen'}
                </h2>
              </div>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: T.muted, lineHeight: 1, padding: 4 }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '28px 32px 32px' }}>
              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b91c1c', marginBottom: 16 }}>
                  Fehler: {error}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label="Mieter *">
                  <select required value={form.tenant_id} onChange={e => handleTenantChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">– Mieter auswählen –</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>{t.last_name}, {t.first_name}{t.unit ? ` (${t.unit})` : ''}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Objekt">
                  <select value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">– kein Objekt –</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label="Kautionsbetrag (€) *">
                  <input type="number" min="0" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Einzahlungsdatum">
                  <input type="date" value={form.paid_date} onChange={e => setForm({ ...form, paid_date: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <Field label="Status">
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="hinterlegt">Hinterlegt</option>
                  <option value="teilweise">Teilweise zurückgezahlt</option>
                  <option value="zurückgezahlt">Zurückgezahlt</option>
                </select>
              </Field>

              {(form.status === 'teilweise' || form.status === 'zurückgezahlt') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Field label="Rückzahlungsdatum">
                    <input type="date" value={form.return_date} onChange={e => setForm({ ...form, return_date: e.target.value })} style={inputStyle} />
                  </Field>
                  <Field label="Zurückgezahlter Betrag (€)">
                    <input type="number" min="0" step="0.01" value={form.return_amount} onChange={e => setForm({ ...form, return_amount: e.target.value })} style={inputStyle} />
                  </Field>
                </div>
              )}

              <Field label="Notizen">
                <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionale Anmerkungen..." style={{ ...inputStyle, resize: 'vertical' }} />
              </Field>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} className="btn-primary"
                  style={{ flex: 1, height: 44, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={closeForm} className="btn-secondary"
                  style={{ height: 44, fontSize: 14, padding: '0 20px' }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
